import { completeConfig } from '@/components/ui/data-table/config';
import { DataTable } from '@/components/ui/data-table/data-table';
import { DataTableConsumer } from '@/components/ui/data-table/types';
import { cacheEntriesTableConfig } from '@/config/table-configs';
import { cacheEntryFields } from '@/features/cache/cache.fields';
import { useFilterableTable } from '@/hooks/useFilterableTable';
import { usePanelFilterState } from '@/hooks/usePanelFilterState';
import { usePersistedTableConfig } from '@/hooks/usePersistedTableConfig';
import { formatBytes } from '@/lib/formatBytes';
import { CacheEntry } from '@certhon/models';
import { ColumnDef } from '@tanstack/react-table';
import { formatDistanceToNowStrict } from 'date-fns';
import { useCallback, useMemo } from 'react';

const emptyArray: CacheEntry[] = [];

function AgeCell({ value }: { value: string }) {
  return (
    <span title={new Date(value).toLocaleString()}>
      {formatDistanceToNowStrict(new Date(value), { addSuffix: true })}
    </span>
  );
}

function BytesCell({ value }: { value: number }) {
  return <span className="tabular-nums">{formatBytes(value)}</span>;
}

const customColumns: ColumnDef<CacheEntry>[] = [
  {
    id: 'query',
    header: 'Query',
    accessorKey: 'query',
    cell: ({ row }) => (
      <code className="text-xs break-all" title={row.original.query}>
        {decodeURIComponent(row.original.query)}
      </code>
    ),
  },
  {
    id: 'size',
    header: 'Size',
    accessorKey: 'size',
    cell: ({ row }) => <BytesCell value={row.original.size} />,
  },
  {
    id: 'stored_size',
    header: 'Stored',
    accessorKey: 'stored_size',
    cell: ({ row }) => <BytesCell value={row.original.stored_size} />,
  },
  {
    id: 'last_hit',
    header: 'Last hit',
    accessorKey: 'last_hit',
    cell: ({ row }) => <AgeCell value={row.original.last_hit} />,
  },
  {
    id: 'created',
    header: 'Created',
    accessorKey: 'created',
    cell: ({ row }) => <AgeCell value={row.original.created} />,
  },
  {
    id: 'key',
    header: 'ETag',
    accessorKey: 'key',
    cell: ({ row }) => (
      <code className="text-muted-foreground text-xs">{row.original.key}</code>
    ),
  },
];

export function CacheEntriesTable({
  uriPrefix = '',
  data,
  isLoading,
}: { uriPrefix?: string } & DataTableConsumer<CacheEntry>) {
  const { filterValue, setFilterValue } = usePanelFilterState();

  const {
    tokenDefinitions,
    filteredData,
    columns,
    isFiltering,
    resultCount,
    totalCount,
  } = useFilterableTable<CacheEntry>({
    fields: cacheEntryFields,
    data: data || emptyArray,
    filterValue,
    customColumns,
  });

  const completeDefaultConfig = useMemo(
    () => completeConfig(cacheEntriesTableConfig, columns),
    [columns],
  );

  const [tableConfig, setTableConfig] = usePersistedTableConfig(
    'cache-entries',
    completeDefaultConfig,
  );

  // Entries have no detail panel; a row click stays put.
  const createUriPath = useCallback(() => `${uriPrefix}/cache`, [uriPrefix]);

  return (
    <DataTable<CacheEntry, unknown>
      isLoading={isLoading}
      data={filteredData}
      columns={columns}
      config={tableConfig}
      onConfigChange={setTableConfig}
      createUriPath={createUriPath}
      getRowId={(entry) => entry.key}
      filterValue={filterValue}
      onFilterChange={setFilterValue}
      tokenDefinitions={tokenDefinitions}
      isFiltering={isFiltering}
      resultCount={resultCount}
      totalCount={totalCount}
      entityName="cache entries"
    />
  );
}

export default CacheEntriesTable;
